/**
 * The credit-pack checkout: our order summary beside Paddle's INLINE checkout
 * frame, in a Lorvok-styled dialog (see lib/paddle.ts for why inline). Opening
 * it opens the checkout for a transaction the API already created (or a
 * `?_ptxn=` payment link); closing it — × or Esc — closes Paddle's checkout too.
 *
 * The summary starts from the pack's list price with a "tax at checkout" hint,
 * and switches to Paddle's own subtotal / tax / total as soon as the frame
 * reports them (`checkout.loaded` / `checkout.updated`), so the buyer sees the
 * exact amount incl. VAT for their country. Paddle's Merchant-of-Record footer
 * stays visible inside the frame. The credits themselves arrive via the webhook;
 * the page refreshes the balance on `checkout.completed`.
 */
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CreditIcon } from "./CreditIcon.js";
import { formatCents, formatCredits } from "../lib/credits.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import {
  CHECKOUT_FRAME_CLASS,
  blockedBillingCountry,
  closePaddleCheckout,
  onPaddleEvent,
  openPaddleCheckout
} from "../lib/paddle.js";

/** What the buyer picked on the pricing page (absent for a payment link). */
export interface CheckoutSummary {
  /** The pack's tier name ("Creator"). */
  title: string;
  creditsCents: number;
  /** Credits over the list rate, in percent (0 = none). */
  bonusPercent: number;
  /** List price before tax, in the currency's minor units. */
  priceCents: number;
  currency: string;
}

interface Totals {
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
}

type Status = "loading" | "ready" | "completed" | "blocked" | "error";

/** An amount in MAJOR units (Paddle's event totals) in the reader's locale. */
function formatMoney(amount: number, currency: string, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function CheckoutModal({
  environment,
  transactionId,
  email = null,
  summary = null,
  onClose
}: {
  environment: "sandbox" | "production";
  transactionId: string;
  email?: string | null;
  summary?: CheckoutSummary | null;
  onClose: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [productName, setProductName] = useState<string | null>(null);

  // Read inside the open effect without re-opening the checkout when they change
  // (a language switch mid-payment must not reset the form).
  const openArgsRef = useRef({ email, lang, t });
  openArgsRef.current = { email, lang, t };
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Subscribe first, then open, so the frame's first `checkout.loaded` is heard.
  useEffect(() => {
    const unsubscribe = onPaddleEvent((event) => {
      if (blockedBillingCountry(event)) {
        // lib/paddle.ts has already closed the checkout; say why, here.
        setStatus("blocked");
        return;
      }
      if (event.name === "checkout.completed") {
        setStatus("completed");
        return;
      }
      if (event.name === "checkout.loaded" || event.name === "checkout.updated") {
        setStatus((current) => (current === "loading" ? "ready" : current));
        const data = event.data;
        const eventTotals = data?.totals;
        if (data?.currency_code && typeof eventTotals?.total === "number") {
          setTotals({
            subtotal: eventTotals.subtotal ?? eventTotals.total - (eventTotals.tax ?? 0),
            tax: eventTotals.tax ?? 0,
            total: eventTotals.total,
            currency: data.currency_code
          });
        }
        const name = data?.items?.[0]?.product?.name?.trim();
        if (name) setProductName(name);
      }
    });
    // Aborted on close: Paddle.js may still be loading, and a late open would
    // put a checkout into a frame that is gone.
    const controller = new AbortController();
    const { email: buyerEmail, lang: locale } = openArgsRef.current;
    openPaddleCheckout(environment, transactionId, { email: buyerEmail, locale, signal: controller.signal }).catch(
      (err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus("error");
        setError(err instanceof Error ? err.message : openArgsRef.current.t("pricing.checkoutFailed"));
      }
    );
    return () => {
      controller.abort();
      unsubscribe();
      closePaddleCheckout();
    };
  }, [environment, transactionId]);

  // Initial focus on the dialog, Esc closes, focus returns to the Buy button.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closePaddleCheckout();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  function close() {
    closePaddleCheckout();
    onClose();
  }

  // Focus trap: Tab past either end of the dialog wraps around. Sentinels (not a
  // keydown handler) because focus inside Paddle's iframe never reaches our
  // listeners — leaving the frame's last field lands on the end sentinel.
  function wrapToStart() {
    closeButtonRef.current?.focus();
  }
  function wrapToEnd() {
    const frame = frameRef.current?.querySelector("iframe");
    if (frame && status !== "blocked") frame.focus();
    else closeButtonRef.current?.focus();
  }

  // The tier name alone ("Creator") reads like a role, so it is named as a pack.
  const title = summary
    ? t("checkout.packTitle", { name: summary.title })
    : productName ?? t("checkout.paymentLink");
  const showFrame = status === "loading" || status === "ready" || status === "completed";

  return createPortal(
    <div className="admin-modal-backdrop checkout-modal-backdrop">
      <span tabIndex={0} className="sr-only" onFocus={wrapToEnd} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="admin-modal checkout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="admin-modal-close"
          aria-label={t("popup.close")}
          onClick={close}
        >
          ×
        </button>

        <section className="checkout-summary" aria-label={t("checkout.summary")}>
          <p className="checkout-eyebrow">{t("checkout.summary")}</p>
          <h2 id={titleId} className="checkout-title">
            {title}
          </h2>
          {summary && (
            <div className="checkout-credits">
              <span className="checkout-credits-figure">
                <CreditIcon size={22} />
                {formatCredits(summary.creditsCents, t)}
              </span>
              {summary.bonusPercent > 0 && (
                <span className="pricing-pack-bonus">{t("pricing.bonus", { percent: summary.bonusPercent })}</span>
              )}
            </div>
          )}

          {totals ? (
            <dl className="checkout-totals" data-testid="checkout-totals">
              <div>
                <dt>{t("checkout.subtotal")}</dt>
                <dd>{formatMoney(totals.subtotal, totals.currency, lang)}</dd>
              </div>
              <div>
                <dt>{t("checkout.tax")}</dt>
                <dd>{formatMoney(totals.tax, totals.currency, lang)}</dd>
              </div>
              <div className="checkout-total-row">
                <dt>{t("checkout.total")}</dt>
                <dd>{formatMoney(totals.total, totals.currency, lang)}</dd>
              </div>
            </dl>
          ) : summary ? (
            <>
              <dl className="checkout-totals">
                <div className="checkout-total-row">
                  <dt>{t("checkout.listPrice")}</dt>
                  <dd>
                    {formatCents(summary.priceCents)} <span className="pricing-currency">{summary.currency}</span>
                  </dd>
                </div>
              </dl>
              <p className="checkout-hint">{t("checkout.taxHint")}</p>
            </>
          ) : null}

          {status === "completed" && (
            <div className="checkout-success" role="status">
              <p>{t("checkout.success")}</p>
              <button type="button" onClick={close}>
                {t("checkout.done")}
              </button>
            </div>
          )}
          {status === "blocked" && (
            <p className="checkout-alert" role="alert">
              {t("pricing.regionUnavailable")}
            </p>
          )}
          {status === "error" && error && (
            <p className="checkout-alert" role="alert">
              {error}
            </p>
          )}

          <p className="checkout-mor">{t("checkout.mor")}</p>
        </section>

        <div className="checkout-pay" hidden={!showFrame}>
          {status === "loading" && (
            <p className="checkout-loading" role="status">
              <span className="btn-spinner" aria-hidden="true" />
              {t("checkout.loading")}
            </p>
          )}
          {/* Paddle.js owns this element's children (its iframe): keep it empty in React. */}
          <div ref={frameRef} className={`${CHECKOUT_FRAME_CLASS} checkout-frame`} />
        </div>
      </div>
      <span tabIndex={0} className="sr-only" onFocus={wrapToStart} />
    </div>,
    document.body
  );
}
